import { getPostHog } from "../posthog";
import { CaptureResult } from "posthog-js";

export type ErrorSource =
  | "react_boundary"
  | "window_error"
  | "unhandled_rejection";

export interface ReportErrorProps {
  error_source: ErrorSource;
  user_generated?: boolean;
  [key: string]: unknown;
}

const handledErrors = new WeakSet<Error>();

const ignoredErrorClasses = new Set<string>(["ChunkLoadError"]);

const ignoredErrorMessageRegexes: RegExp[] = [
  /^attempted to hard navigate to the same URL/,
  /^Load failed$/,
  /^Unexpected token '<'$/,
  /^\$ is not defined$/,
  /^Can't find variable: \$$/,
  /^Script error\.$/,
];

export function beforeSend(event: CaptureResult): CaptureResult | null {
  if (event?.event !== "$exception") return event;
  const ex = event?.properties?.$exception_list?.[0];
  if (!ex) return event;

  if (ignoredErrorClasses.has(ex.type)) return null;

  if (ignoredErrorMessageRegexes.some((r) => r.test(ex.value))) return null;

  const frames = ex.stacktrace?.frames ?? [];
  if (
    frames.some((f) =>
      /DangerouslySetHtmlContent/.test(f.function ?? f.filename ?? ""),
    )
  )
    return null;

  return event;
}

function normalizeError(input: unknown): Error {
  if (input instanceof Error) return input;
  if (typeof input === "string") return new Error(input);
  try {
    return new Error(JSON.stringify(input));
  } catch {
    return new Error(String(input));
  }
}

export function reportError(error: unknown, props: ReportErrorProps): void {
  const err = normalizeError(error);

  if (handledErrors.has(err)) return;
  handledErrors.add(err);

  getPostHog().captureException(err, props as Record<string, unknown>);
}

let listenersWired = false;

export function wireGlobalErrorListeners(): void {
  if (typeof window === "undefined") return;
  if (listenersWired) return;
  listenersWired = true;

  window.addEventListener("error", (event) => {
    reportError(event.error ?? new Error(event.message || "window.error"), {
      error_source: "window_error",
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    reportError(event.reason, { error_source: "unhandled_rejection" });
  });
}

export const __testing = {
  resetListenersWired: () => {
    listenersWired = false;
  },
};
